import { Loading } from "components/ui/Loading/Loading";
/**
 * マッチング設定 作成/編集フォーム（Presentation）
 */

import { Button } from "components/ui/Button";
import type {
  CreateMatchingConditionInput,
  MatchingCondition,
  NotificationRecipient,
} from "hooks/use-matching-conditions-api";
import type { WorkspaceMember } from "hooks/use-workspace-members-api";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EmailRecipientPicker } from "routes/settings/MatchingConditionFormPage/EmailRecipientPicker";

type MatchingConditionFormPageProps = {
  isEdit: boolean;
  initialData?: MatchingCondition | null;
  loading: boolean;
  onSubmit: (input: CreateMatchingConditionInput) => Promise<void>;
  isSubmitting: boolean;
  workspaceMembers: WorkspaceMember[];
};

type FormData = {
  name: string;
  description: string;
  matchingMode: string;
  minMargin: string;
  maxEmailAgeDays: string;
  maxDepth: string;
  minTotalScorePercent: string;
  slackNotificationEnabled: boolean;
  emailNotificationEnabled: boolean;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export const MatchingConditionFormPage: FC<MatchingConditionFormPageProps> = ({
  isEdit,
  initialData,
  loading,
  onSubmit,
  isSubmitting,
  workspaceMembers,
}) => {
  const [formData, setFormData] = useState<FormData>(() => ({
    name: initialData?.name ?? "",
    description: initialData?.description ?? "",
    matchingMode: initialData?.matchingMode ?? "manual",
    minMargin: initialData ? String(initialData.minMargin) : "50000",
    maxEmailAgeDays:
      initialData?.maxEmailAgeDays != null
        ? String(initialData.maxEmailAgeDays)
        : "",
    maxDepth: initialData?.maxDepth != null ? String(initialData.maxDepth) : "",
    minTotalScorePercent: initialData
      ? String(Math.round(initialData.minTotalScore * 100))
      : "50",
    slackNotificationEnabled: initialData?.slackNotificationEnabled ?? false,
    emailNotificationEnabled: initialData?.emailNotificationEnabled ?? false,
  }));

  const [emailRecipients, setEmailRecipients] = useState<
    NotificationRecipient[]
  >(initialData?.emailNotificationRecipients ?? []);

  // 編集モードで initialData がロードされたらフォームを更新
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description ?? "",
        matchingMode: initialData.matchingMode,
        minMargin: String(initialData.minMargin),
        maxEmailAgeDays:
          initialData.maxEmailAgeDays != null
            ? String(initialData.maxEmailAgeDays)
            : "",
        maxDepth:
          initialData.maxDepth != null ? String(initialData.maxDepth) : "",
        minTotalScorePercent: String(
          Math.round(initialData.minTotalScore * 100),
        ),
        slackNotificationEnabled: initialData.slackNotificationEnabled,
        emailNotificationEnabled: initialData.emailNotificationEnabled,
      });
      setEmailRecipients(initialData.emailNotificationRecipients ?? []);
    }
  }, [initialData]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "設定名は必須です";
    } else if (formData.name.length > 255) {
      newErrors.name = "設定名は255文字以内で入力してください";
    }

    const margin = Number(formData.minMargin);
    if (formData.minMargin === "" || Number.isNaN(margin)) {
      newErrors.minMargin = "最低マージンを入力してください";
    } else if (margin < 0) {
      newErrors.minMargin = "0以上の値を入力してください";
    }

    if (formData.maxEmailAgeDays !== "") {
      const days = Number(formData.maxEmailAgeDays);
      if (Number.isNaN(days) || days < 1) {
        newErrors.maxEmailAgeDays = "1以上の値を入力してください";
      }
    }

    if (formData.maxDepth !== "") {
      const depth = Number(formData.maxDepth);
      if (Number.isNaN(depth) || depth < 1) {
        newErrors.maxDepth = "1以上の値を入力してください";
      }
    }

    const score = Number(formData.minTotalScorePercent);
    if (formData.minTotalScorePercent === "" || Number.isNaN(score)) {
      newErrors.minTotalScorePercent = "最低スコアを入力してください";
    } else if (score < 0 || score > 100) {
      newErrors.minTotalScorePercent = "0〜100の値を入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitError(null);
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        matchingMode: formData.matchingMode,
        minMargin: Number(formData.minMargin),
        maxEmailAgeDays:
          formData.maxEmailAgeDays !== ""
            ? Number(formData.maxEmailAgeDays)
            : null,
        maxDepth: formData.maxDepth !== "" ? Number(formData.maxDepth) : null,
        minTotalScore: Number(formData.minTotalScorePercent) / 100,
        slackNotificationEnabled: formData.slackNotificationEnabled,
        emailNotificationEnabled: formData.emailNotificationEnabled,
        emailNotificationRecipients: formData.emailNotificationEnabled
          ? emailRecipients
          : [],
      });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "保存に失敗しました");
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {isEdit ? "マッチング設定を編集" : "マッチング設定を作成"}
        </h1>
        <p className="text-base-content/60 mt-1">
          マッチング実行時に使用する条件を設定します
        </p>
      </div>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 設定名 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  設定名 <span className="text-error">*</span>
                </span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => handleChange("name", e.target.value)}
                className={`input input-bordered input-sm ${errors.name ? "input-error" : ""}`}
                placeholder="例: デフォルト設定"
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.name}
                  </span>
                </label>
              )}
            </div>

            {/* 説明 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">説明</span>
              </label>
              <textarea
                value={formData.description}
                onChange={e => handleChange("description", e.target.value)}
                className="textarea textarea-bordered"
                rows={2}
                placeholder="設定の説明（任意）"
              />
            </div>

            <div className="divider my-2" />

            {/* マッチングモード */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  マッチングモード <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">マッチング結果の処理方法</span>
              </label>
              <div className="flex gap-4">
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="manual"
                    checked={formData.matchingMode === "manual"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">手動</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="review"
                    checked={formData.matchingMode === "review"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">レビュー</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="auto"
                    checked={formData.matchingMode === "auto"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">自動</span>
                </label>
              </div>
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  手動: ユーザーが結果を確認して提案 / レビュー: AI提案を承認 /
                  自動: AI判定で自動提案
                </span>
              </label>
            </div>

            {/* 最低マージン */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  最低マージン（円） <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">BP案件での最低利益額</span>
              </label>
              <input
                type="number"
                value={formData.minMargin}
                onChange={e => handleChange("minMargin", e.target.value)}
                className={`input input-bordered input-sm ${errors.minMargin ? "input-error" : ""}`}
                min={0}
                step={10000}
                placeholder="50000"
              />
              {errors.minMargin && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.minMargin}
                  </span>
                </label>
              )}
            </div>

            {/* 最低総合スコア */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  最低総合スコア（%） <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">
                  この値未満の結果は除外されます
                </span>
              </label>
              <input
                type="number"
                value={formData.minTotalScorePercent}
                onChange={e =>
                  handleChange("minTotalScorePercent", e.target.value)
                }
                className={`input input-bordered input-sm ${errors.minTotalScorePercent ? "input-error" : ""}`}
                min={0}
                max={100}
                step={5}
                placeholder="50"
              />
              {errors.minTotalScorePercent && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.minTotalScorePercent}
                  </span>
                </label>
              )}
            </div>

            {/* メール有効期間 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">メール有効期間（日）</span>
                <span className="label-text-alt">空欄 = 制限なし</span>
              </label>
              <input
                type="number"
                value={formData.maxEmailAgeDays}
                onChange={e => handleChange("maxEmailAgeDays", e.target.value)}
                className={`input input-bordered input-sm ${errors.maxEmailAgeDays ? "input-error" : ""}`}
                min={1}
                placeholder="制限なし"
              />
              {errors.maxEmailAgeDays && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.maxEmailAgeDays}
                  </span>
                </label>
              )}
            </div>

            {/* 商流の深さ上限 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">商流の深さ上限</span>
                <span className="label-text-alt">空欄 = 制限なし</span>
              </label>
              <input
                type="number"
                value={formData.maxDepth}
                onChange={e => handleChange("maxDepth", e.target.value)}
                className={`input input-bordered input-sm ${errors.maxDepth ? "input-error" : ""}`}
                min={1}
                placeholder="制限なし"
              />
              {errors.maxDepth && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.maxDepth}
                  </span>
                </label>
              )}
            </div>

            <div className="divider my-2" />

            {/* 通知設定 */}
            <h3 className="font-medium text-base">通知設定</h3>

            {/* メール通知 */}
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={formData.emailNotificationEnabled}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      emailNotificationEnabled: e.target.checked,
                    }))
                  }
                />
                <div>
                  <span className="label-text font-medium">メール通知</span>
                  <p className="text-base-content/60 text-sm">
                    マッチング成功時にメールで通知します
                  </p>
                </div>
              </label>
            </div>

            {formData.emailNotificationEnabled && (
              <div className="ml-4 pl-4 border-l-2 border-primary/20">
                <EmailRecipientPicker
                  recipients={emailRecipients}
                  members={workspaceMembers}
                  onAdd={r => setEmailRecipients(prev => [...prev, r])}
                  onRemove={email =>
                    setEmailRecipients(prev =>
                      prev.filter(r => r.email !== email),
                    )
                  }
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* Slack通知 */}
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={formData.slackNotificationEnabled}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      slackNotificationEnabled: e.target.checked,
                    }))
                  }
                />
                <div>
                  <span className="label-text font-medium">Slack通知</span>
                  <p className="text-base-content/60 text-sm">
                    マッチング成功時にSlackチャンネルへ通知します
                  </p>
                </div>
              </label>
            </div>

            {submitError && (
              <div className="alert alert-error">
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button asChild variant="ghost">
                <Link to="/settings/matching/conditions">キャンセル</Link>
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loading size="sm" />
                ) : isEdit ? (
                  "更新"
                ) : (
                  "作成"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
